import { FinancialApplication, FinancialPerson } from '@/api/financial/application/read-application'
import { removeNonNumeric } from '@/helpers'
import { AllowanceStep, isMarried } from './allowance-flow'

export type AllowanceForm = {
  collegeKey: string
  collegePrivate: boolean
  tuition: string
  graduation: string
  amount: number
  grace: boolean
  combo: boolean
  birthdate: string
  gender: string
  zip_code: string
  street: string
  district: string
  number: string
  complement: string
  municipality_id: string
  marital: string
  spouse_name: string
  spouse_tax_id: string
  politically_exposed: string
  us_person: string
  father_not_registered: boolean
  father_name: string
  father_tax_id: string
  father_education_level: string
  mother_name: string
  mother_tax_id: string
  mother_education_level: string
  purposes: string[]
  purpose_other: string
  scholarships: string[]
  scholarships_other: string
  scholarship_percentage: string
  financing: string[]
  financing_other: string
  financing_percentage: string
  payment_sources: string[]
  income: string
  free_text: string
  high_school_type: string
  academic_award: string
  academic_award_description: string
  course_locked: string
  course_locked_reason: string
  plans: string[]
  residency_timing: string
  bank_code: string
  agency_number: string
  agency_digit: string
  account_number: string
  account_digit: string
  account_type: string
}

export function emptyForm(): AllowanceForm {
  return {
    collegeKey: '',
    collegePrivate: false,
    tuition: '',
    graduation: '',
    amount: 1000,
    grace: true,
    combo: true,
    birthdate: '',
    gender: '',
    zip_code: '',
    street: '',
    district: '',
    number: '',
    complement: '',
    municipality_id: '',
    marital: '',
    spouse_name: '',
    spouse_tax_id: '',
    politically_exposed: '',
    us_person: '',
    father_not_registered: false,
    father_name: '',
    father_tax_id: '',
    father_education_level: '',
    mother_name: '',
    mother_tax_id: '',
    mother_education_level: '',
    purposes: [],
    purpose_other: '',
    scholarships: [],
    scholarships_other: '',
    scholarship_percentage: '',
    financing: [],
    financing_other: '',
    financing_percentage: '',
    payment_sources: [],
    income: '',
    free_text: '',
    high_school_type: '',
    academic_award: '',
    academic_award_description: '',
    course_locked: '',
    course_locked_reason: '',
    plans: [],
    residency_timing: '',
    bank_code: '',
    agency_number: '',
    agency_digit: '',
    account_number: '',
    account_digit: '',
    account_type: '',
  }
}

function yesNo(value: boolean | null | undefined) {
  if (value == null) return ''
  return value ? 'yes' : 'no'
}

function text(value: string | number | null | undefined) {
  if (value == null) return ''
  return String(value)
}

export function formFromApplication(
  application: FinancialApplication,
  graduation: string | null | undefined,
): AllowanceForm {
  const person: FinancialPerson | undefined = application.application_persons[0]?.person
  const address = person?.address[0]
  const bank = person?.bank_account[0]
  const base = emptyForm()
  return {
    ...base,
    collegeKey: application.college?.key ?? '',
    collegePrivate: application.college?.college_type === 'private',
    tuition: text(person?.monthly_tuition),
    graduation: graduation?.slice(0, 7) ?? '',
    amount: application.simulation ? Number(application.simulation.amount) : 1000,
    grace: application.simulation ? application.simulation.grace_period > 0 : true,
    combo: application.simulation?.is_combo ?? true,
    birthdate: person?.birthdate?.slice(0, 10) ?? '',
    gender: person?.gender ?? '',
    zip_code: address?.zip_code ?? '',
    street: address?.street ?? '',
    district: address?.district ?? '',
    number: address?.number ?? '',
    complement: address?.complement ?? '',
    municipality_id: address ? String(address.municipality_id) : '',
    marital: person?.marital_status ?? '',
    spouse_name: person?.spouse_name ?? '',
    spouse_tax_id: person?.spouse_tax_id ?? '',
    politically_exposed: yesNo(person?.politically_exposed),
    us_person: yesNo(person?.us_person),
    father_not_registered: Boolean(person?.father_not_registered),
    father_name: person?.father_name ?? '',
    father_tax_id: person?.father_tax_id ?? '',
    father_education_level: person?.father_education_level ?? '',
    mother_name: person?.mother_name ?? '',
    mother_tax_id: person?.mother_tax_id ?? '',
    mother_education_level: person?.mother_education_level ?? '',
    purposes: person?.credit_purposes ?? [],
    purpose_other: person?.credit_purpose_other ?? '',
    scholarships: person?.scholarships ?? [],
    scholarships_other: person?.scholarships_other ?? '',
    scholarship_percentage: text(person?.scholarship_percentage),
    financing: person?.student_financing ?? [],
    financing_other: person?.student_financing_other ?? '',
    financing_percentage: text(person?.student_financing_percentage),
    payment_sources: person?.tuition_payment_sources ?? [],
    income: text(person?.monthly_family_income),
    free_text: person?.free_text ?? '',
    high_school_type: person?.high_school_type ?? '',
    academic_award: yesNo(person?.academic_award),
    academic_award_description: person?.academic_award_description ?? '',
    course_locked: yesNo(person?.course_locked),
    course_locked_reason: person?.course_locked_reason ?? '',
    plans: person?.post_graduation_plans ?? [],
    residency_timing: person?.residency_timing ?? '',
    bank_code: bank?.bank_code ?? '',
    agency_number: bank?.agency_number ?? '',
    agency_digit: bank?.agency_digit ?? '',
    account_number: bank?.account_number ?? '',
    account_digit: bank?.account_digit ?? '',
    account_type: bank?.account_type ?? '',
  }
}

export const digits = removeNonNumeric

export function personPayloadForStep(
  step: AllowanceStep,
  form: AllowanceForm,
): Record<string, unknown> | null {
  switch (step) {
    case 'eu':
      return { birthdate: form.birthdate, gender: form.gender }
    case 'end':
      return {
        address: {
          zip_code: digits(form.zip_code),
          street: form.street,
          number: form.number,
          complement: form.complement,
          district: form.district,
          municipality_id: Number(form.municipality_id),
        },
      }
    case 'civil': {
      const married = isMarried(form.marital)
      return {
        marital_status: form.marital,
        politically_exposed: form.politically_exposed === 'yes',
        us_person: form.us_person === 'yes',
        spouse_name: married ? form.spouse_name : null,
        spouse_tax_id: married ? digits(form.spouse_tax_id) : null,
      }
    }
    case 'pais':
      return {
        father_not_registered: form.father_not_registered,
        father_name: form.father_not_registered ? null : form.father_name,
        father_tax_id: form.father_not_registered ? null : digits(form.father_tax_id),
        father_education_level: form.father_not_registered ? null : form.father_education_level,
        mother_name: form.mother_name,
        mother_tax_id: digits(form.mother_tax_id),
        mother_education_level: form.mother_education_level,
      }
    case 'uso':
      return {
        credit_purposes: form.purposes,
        credit_purpose_other: form.purpose_other || null,
      }
    case 'renda':
      return {
        ...(form.collegePrivate
          ? {
              scholarships: form.scholarships,
              scholarships_other: form.scholarships_other || null,
              scholarship_percentage: form.scholarship_percentage
                ? Number(form.scholarship_percentage)
                : null,
              student_financing: form.financing,
              student_financing_other: form.financing_other || null,
              student_financing_percentage: form.financing_percentage
                ? Number(form.financing_percentage)
                : null,
              tuition_payment_sources: form.payment_sources,
            }
          : {}),
        monthly_family_income: Number(form.income),
      }
    case 'msg':
      return { free_text: form.free_text || null }
    case 'traj':
      return {
        high_school_type: form.high_school_type,
        academic_award: form.academic_award === 'yes',
        academic_award_description: form.academic_award_description || null,
        course_locked: form.course_locked === 'yes',
        course_locked_reason: form.course_locked_reason || null,
      }
    case 'planos':
      return {
        post_graduation_plans: form.plans,
        residency_timing: form.residency_timing,
      }
    default:
      return null
  }
}

export function isStepValid(
  step: AllowanceStep,
  form: AllowanceForm,
  context: {
    semester: number
    minSemester: number
    limits?: { min: number; max: number } | null
    hasPreview: boolean
    hasRequiredDocs: boolean
    hasProposal: boolean
    denied: boolean
    hasVerifiedPhone: boolean
  },
): boolean {
  const tuition = Number(form.tuition)
  switch (step) {
    case 'fac':
      return Boolean(
        form.collegeKey &&
          form.graduation &&
          context.semester >= context.minSemester &&
          (!form.collegePrivate || (tuition >= 3000 && tuition <= 18000)),
      )
    case 'valor':
      return Boolean(
        context.limits &&
          form.amount >= context.limits.min &&
          form.amount <= context.limits.max,
      )
    case 'plano':
      return context.hasPreview
    case 'eu':
      return Boolean(context.hasVerifiedPhone && form.birthdate && form.gender)
    case 'end':
      return Boolean(
        form.zip_code &&
          form.street &&
          form.number &&
          form.district &&
          form.municipality_id,
      )
    case 'civil':
      return Boolean(
        form.marital &&
          form.politically_exposed &&
          form.us_person &&
          (!isMarried(form.marital) ||
            (form.spouse_name && digits(form.spouse_tax_id).length === 11)),
      )
    case 'pais':
      return Boolean(
        form.mother_name &&
          digits(form.mother_tax_id).length === 11 &&
          form.mother_education_level &&
          (form.father_not_registered ||
            (form.father_name &&
              digits(form.father_tax_id).length === 11 &&
              form.father_education_level)),
      )
    case 'uso':
      return (
        form.purposes.length > 0 &&
        (!form.purposes.includes('other') || Boolean(form.purpose_other))
      )
    case 'renda':
      return form.income !== ''
    case 'traj':
      return Boolean(
        form.high_school_type &&
          form.academic_award &&
          form.course_locked &&
          (form.academic_award !== 'yes' || form.academic_award_description) &&
          (form.course_locked !== 'yes' || form.course_locked_reason),
      )
    case 'planos':
      return Boolean(form.residency_timing)
    case 'conta':
      return Boolean(
        form.bank_code &&
          form.agency_number &&
          form.account_number &&
          form.account_digit &&
          form.account_type,
      )
    case 'docs':
      return context.hasRequiredDocs
    case 'assinar':
      return false
    case 'analise':
      return !context.denied
    case 'proposta':
      return context.hasProposal
    default:
      return true
  }
}

